// src/components/MachineContextSeed.jsx

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDataLayer } from '@quantiix/data-layer';
import { useAuth } from '../contexts/AuthContext';
import { createLogger } from '../utils/logger';

const logger = createLogger('Shell::MachineContextSeed');

/**
 * Deep links to /app/machine/<machine_id> arrive with no navigation context —
 * the data-layer scope is only populated by drill-down or a restored (same-tab)
 * session. Without a companyId the Machine MFE can't fetch the hierarchy it needs
 * to resolve factory/OU, so it would dead-end on "missing context" (F-006).
 *
 * For a user with exactly one assigned company we can seed companyId from their
 * profile; the MFE then resolves factory/OU from the hierarchy itself. Superusers
 * and multi-company users have no unambiguous company at this point, so we leave
 * the scope empty and let the MFE render an explicit not-found/no-access state
 * (resolving those is a follow-up — would need a backend machine→hierarchy lookup).
 *
 * Side-effect only; renders nothing. Never overrides an already-populated scope,
 * so the normal drill-down / refresh paths are untouched.
 */
export const MachineContextSeed = () => {
    const { isAuthenticated, loading, isSuperuser, permissions } = useAuth();
    const { companyId, setScope } = useDataLayer();
    const location = useLocation();

    useEffect(() => {
        if (loading || !isAuthenticated) return;
        if (!location.pathname.startsWith('/app/machine/')) return;
        if (companyId) return; // context already present (drill-down or restored session)

        const companies = permissions.company || [];
        // Scope to single-company users for now; superuser/multi-company is ambiguous.
        if (isSuperuser || companies.length !== 1) return;

        const only = companies[0];
        const resolvedCompanyId = typeof only === 'object' ? only.id : only;
        if (!resolvedCompanyId) return;

        logger.info('Seeding companyId for machine deep link', {
            companyId: resolvedCompanyId,
            path: location.pathname,
        });
        setScope({ companyId: resolvedCompanyId });
    }, [isAuthenticated, loading, isSuperuser, permissions, companyId, location.pathname, setScope]);

    return null;
};

export default MachineContextSeed;
